/**
 * 📖 **어디까지 읽었나** — 이 기기 안에만 남는다.
 *
 * ## 왜 필요한가
 * 아이는 한 번에 다 안 읽는다. 그런데 다시 들어오면 **책 고르기부터** 다시 해야 했다.
 * 네 살 아이에게 "아까 그 책"을 다시 찾게 하는 건 생각보다 큰 벽이다.
 *
 * ## 서버로 안 보낸다
 * 사람을 알아보려면 기기에 표식을 남겨야 하는데, 그게 이 서비스가 **안 하기로 한 것**이다
 * (불변식 1 — 비회원·무수집). 그래서 여기 남긴 것은 **그 기기에서만** 의미가 있고
 * 서버는 이런 게 있는 줄도 모른다. 폰을 바꾸면 사라지는 게 맞다.
 *
 * ## 지우는 길이 있어야 한다
 * 설정의 「처음부터 다시 하기」가 이것도 지운다 — 남는 걸 만들었으면 지우는 길도 같이 만든다.
 */
const KEY = "gc_reading_v1";

export interface ReadingMark {
  bookId: string;
  /** 사람이 세는 쪽 번호(1부터). 펼침이면 왼쪽 쪽. */
  page: number;
  /** 마지막으로 읽은 시각(ms). 오래된 표시를 걸러내는 데 쓴다. */
  at: number;
}

/** 30일이 지난 표시는 없는 것으로 본다 — 반년 전 책을 "이어서 읽기"로 권하면 엉뚱하다. */
export const MARK_TTL_MS = 30 * 24 * 60 * 60 * 1000;

const isMark = (v: unknown): v is ReadingMark =>
  !!v &&
  typeof v === "object" &&
  typeof (v as ReadingMark).bookId === "string" &&
  (v as ReadingMark).bookId.length > 0 &&
  Number.isFinite((v as ReadingMark).page) &&
  (v as ReadingMark).page >= 1 &&
  Number.isFinite((v as ReadingMark).at);

/**
 * 읽던 자리를 저장한다. 실패해도 조용히 넘어간다 —
 * 사파리 비공개 모드는 localStorage 쓰기가 막힌다. 그 때문에 책이 안 넘어가면 본말이 뒤집힌다.
 */
export function saveMark(bookId: string, page: number, now = Date.now()): void {
  if (!bookId || !Number.isFinite(page) || page < 1) return;
  try {
    localStorage.setItem(KEY, JSON.stringify({ bookId, page: Math.floor(page), at: now }));
  } catch {
    /* 저장 못 해도 읽기는 계속돼야 한다 */
  }
}

/** 이어서 읽을 자리. 없거나·깨졌거나·오래됐으면 null. */
export function loadMark(now = Date.now()): ReadingMark | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const v: unknown = JSON.parse(raw);
    if (!isMark(v)) return null;
    return now - v.at > MARK_TTL_MS ? null : v;
  } catch {
    return null; // 손상된 값 — 없는 것으로
  }
}

/** 설정의 「처음부터 다시 하기」에서 부른다. */
export function clearMark(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* 무시 */
  }
}

/**
 * 첫 쪽이면 이어서 읽을 게 없다 — 표지에서 나간 아이에게 「이어서 읽기」를 권하면
 * "그냥 처음부터잖아?"가 된다. 2쪽부터 의미가 있다.
 */
/**
 * 「이어서 읽기」로 권할 만한가.
 *
 * ① **1쪽이면 안 권한다** — 표지만 보고 나간 것을 「읽던 책」이라 부르지 않는다.
 * ② 🔴 **끝까지 읽었으면 안 권한다.** 다 읽은 책의 표시는 마지막 쪽을 가리킨다. 그걸
 *    「이어서 읽기」로 누르면 **뒤표지에 떨어진다** — 이을 것이 없는 자리다. 게다가 그 쪽에
 *    머무는 한 표시가 계속 갱신돼 **영영 그 상태로 남는다**(2026-08-23 발견).
 *    다 읽었으면 책방(도서관)으로 가는 게 맞다.
 *
 * @param total 그 책의 전체 쪽수. 모르면(옛 목록·못 불러옴) ②는 판정하지 않는다 —
 *              모르는 것을 「다 읽었다」고 우기지 않는다.
 */
export function worthResuming(mark: ReadingMark | null, total?: number): mark is ReadingMark {
  if (!mark || mark.page <= 1) return false;
  if (typeof total === "number" && Number.isFinite(total) && total > 0 && mark.page >= total) return false;
  return true;
}
